/**
 * Catalogue des widgets disponibles : types, tailles (w × h), champs de configuration
 * et valeurs par défaut. Source unique pour le parcours d'ajout (/widgets/*).
 */
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { NEWS_CATEGORY_LABELS } from "../../../api/news";
import { RegulatorWidgetProps } from "./widgets/RegulatorWidget";
import { ChartWidgetProps } from "./widgets/ChartWidget";
import { NewsWidgetProps } from "./widgets/NewsWidget";
import { TextWidgetProps } from "./widgets/TextWidget";
import { WeatherWidgetProps } from "./widgets/WeatherWidget";

export type WidgetTypeId =
  | "RegulatorWidget"
  | "ChartWidget"
  | "WeatherWidget"
  | "ContactsWidget"
  | "AlarmsWidget"
  | "NewsWidget"
  | "TextWidget";

export type WidgetSizeOption = {
  id: string;
  label: string;
  w: number;
  h: number;
  layoutVariant?: RegulatorWidgetProps["layoutVariant"];
};

export type WidgetConfigField = {
  key: string;
  label: string;
  type: "select" | "checkbox" | "citySearch" | "text";
  options?: { value: string; label: string }[];
  /** Placeholder pour un champ texte. */
  placeholder?: string;
  /**
   * Source d'options calculée à l'affichage du configurateur :
   *  - `devices` : les régulateurs accessibles à l'utilisateur (API, filtrés par rôle) ;
   *  - `buildings` : la liste des bâtiments de l'utilisateur (API) ;
   *  - `selectedMetrics` : les grandeurs actuellement cochées (pour désigner les vedettes).
   */
  dynamicOptions?: "devices" | "buildings" | "selectedMetrics";
  /** Type de valeur à stocker pour un select (par défaut string). */
  valueType?: "number";
  /** Icône affichée sur la tuile (cases à cocher / indicateurs). */
  icon?: IconProp;
  defaultValue?: string | boolean;
  /**
   * Regroupement dans le configurateur. `indicators`/`chart` sont les groupes historiques ;
   * toute autre valeur crée une section libre, dont le titre vient de `GROUP_LABELS`.
   */
  group?: string;
};

/**
 * Titres des sections du configurateur. Les deux groupes historiques gardent leurs libellés ;
 * les autres correspondent aux familles de mesures du widget régulateur.
 */
export const GROUP_LABELS: Record<string, string> = {
  indicators: "Indicateurs à afficher",
  chart: "Courbe de tendance",
  ambience: "Ambiance",
  flock: "Cheptel",
  consumption: "Consommation",
  energy: "Énergie",
};

export type WidgetCatalogEntry = {
  id: WidgetTypeId;
  label: string;
  description: string;
  /**
   * Nombre maximum de cases cochables simultanément parmi les champs `indicatorLimitGroups`.
   * Au-delà, les cases non cochées sont désactivées — le widget doit rester lisible sans que
   * l'utilisateur ait à y toucher après coup.
   */
  maxIndicators?: number;
  /** Groupes soumis à `maxIndicators` (les autres champs ne sont pas comptés). */
  indicatorLimitGroups?: string[];
  defaultProps: Record<string, unknown>;
  sizes: WidgetSizeOption[];
  configFields: WidgetConfigField[];
  /**
   * Nombre maximum d'instances de ce widget sur le dashboard (ex. limiter les
   * widgets qui font des appels API). Absent = illimité.
   */
  maxInstances?: number;
  /** Taille minimale en cellules (resize). Absent = bornes globales par défaut. */
  minW?: number;
  minH?: number;
};

// Les valeurs affichées (température, conso, effectif…) ne sont PAS des défauts de
// configuration : elles viennent de l'API à l'affichage (voir useRegulatorMeasures).
// Ne mettre ici que ce que l'utilisateur choisit réellement dans le configurateur.
const REGULATOR_DEFAULTS: RegulatorWidgetProps = {
  name: "Régulateur",
  tag: "Serenity",
  status: "ok",
  // Sélection par défaut : les 6 grandeurs les plus lues en conduite d'élevage.
  metrics: [
    "temperatureInside",
    "humidityInside",
    "co2",
    "animalsTotal",
    "dead",
    "water",
  ],
  // Vedette par défaut : une seule. En mettre deux ici les faisait RÉAPPARAÎTRE après un
  // décochage — `secondaryMetric` absent de la config persistée retombait sur ce défaut
  // (cf. toRuntimeProps, qui étale defaultProps sous display).
  primaryMetric: "temperatureInside",
  showChart: true,
  chartType: "temperatureInside",
  layoutVariant: "full",
};

/** Toutes les grandeurs affichables, dans l'ordre des sections du configurateur. */
export const REGULATOR_METRIC_OPTIONS = [
  // Ambiance
  { value: "temperatureInside", label: "Température int.", group: "ambience", icon: "thermometer-half" },
  { value: "temperatureOutside", label: "Température ext.", group: "ambience", icon: "sun" },
  { value: "humidityInside", label: "Hygrométrie int.", group: "ambience", icon: "tint" },
  { value: "humidityOutside", label: "Hygrométrie ext.", group: "ambience", icon: "droplet" },
  { value: "co2", label: "CO₂", group: "ambience", icon: "smog" },
  { value: "nh3", label: "NH₃", group: "ambience", icon: "smog" },
  { value: "airflow", label: "Débit d'air", group: "ambience", icon: "wind" },
  { value: "light", label: "Éclairement", group: "ambience", icon: "sun" },
  // Cheptel
  { value: "animalsTotal", label: "Effectif", group: "flock", icon: "users" },
  { value: "weight", label: "Poids moyen", group: "flock", icon: "gauge" },
  { value: "gmq", label: "GMQ", group: "flock", icon: "chart-line" },
  { value: "homogeneity", label: "Homogénéité", group: "flock", icon: "arrows-up-down" },
  { value: "panel", label: "Panel pesé", group: "flock", icon: "check" },
  { value: "dead", label: "Mortalité", group: "flock", icon: "triangle-exclamation" },
  // Consommation
  { value: "water", label: "Eau", group: "consumption", icon: "glass-water" },
  { value: "food", label: "Aliment", group: "consumption", icon: "wheat-awn" },
  { value: "waterFeedRatio", label: "Ratio eau / aliment", group: "consumption", icon: "gauge" },
  { value: "silos", label: "Silos (stock)", group: "consumption", icon: "wheat-awn" },
  // Énergie
  { value: "gaz", label: "Gaz", group: "energy", icon: "cloud" },
  { value: "electricity", label: "Électricité", group: "energy", icon: "gauge" },
] as const;

/**
 * Droit d'accès bigdata requis par famille de grandeurs. Miroir de `AccessProjections` côté
 * BIGDATA (Common_Tools_lib/config/access_rights.go) : sans le droit, le bloc est retiré de la
 * réponse par projection d'exclusion — la grandeur n'affichera donc JAMAIS rien.
 *
 * Le cheptel est éclaté : la mortalité dépend de `mortality`, les pesées de `weighing`, et
 * l'effectif d'aucun droit (il est dans `animals.<kind>.total`, jamais masqué).
 */
export const METRIC_ACCESS_RIGHT: Record<string, string | undefined> = {
  temperatureInside: "ambience",
  temperatureOutside: "ambience",
  humidityInside: "ambience",
  humidityOutside: "ambience",
  co2: "ambience",
  nh3: "ambience",
  airflow: "ambience",
  light: "ambience",
  animalsTotal: undefined,
  weight: "weighing",
  gmq: "weighing",
  homogeneity: "weighing",
  panel: "weighing",
  dead: "mortality",
  water: "consumption",
  food: "consumption",
  waterFeedRatio: "consumption",
  silos: "consumption",
  siloStock: "consumption",
  gaz: "energy",
  electricity: "energy",
};

/**
 * Produits legacy du firmware Avibox, dont le widget sait convertir les mesures
 * (`legacyToDailyMeasures`). Les autres legacy (avitouch, vigebox) restent « bientôt disponible ».
 */
export const LEGACY_FIRMWARE_TYPES: readonly string[] = ["avibox", "labelbox"];

export const isLegacyFirmwareType = (deviceType?: string) =>
  deviceType !== undefined && LEGACY_FIRMWARE_TYPES.includes(deviceType);

/**
 * Grandeurs qu'un produit legacy sait fournir, avec leur droit bigdata legacy (masquage par
 * tableau côté BIGDATA, `legacyAccessProjections`). Une grandeur absente d'ici n'est pas proposée
 * au configurateur pour ce produit. En ajouter une = cette ligne + sa lecture dans
 * `legacyToDailyMeasures`.
 */
export const LEGACY_METRIC_ACCESS_RIGHT: Record<string, string> = {
  temperatureInside: "mesure",
  temperatureOutside: "mesure",
  humidityInside: "mesure",
  weight: "pesees",
  gmq: "pesees",
  homogeneity: "pesees",
  dead: "mortality",
  water: "consogeneral",
  food: "consogeneral",
  waterFeedRatio: "consogeneral",
};

/** Le produit sait-il fournir cette grandeur ? Tout produit non legacy-Avibox : oui. */
export const isMetricSupported = (metric: string, deviceType?: string) =>
  !isLegacyFirmwareType(deviceType) || metric in LEGACY_METRIC_ACCESS_RIGHT;

/** Droit bigdata requis par une grandeur, selon la famille du produit. */
export const metricAccessRight = (metric: string, deviceType?: string) =>
  isLegacyFirmwareType(deviceType)
    ? LEGACY_METRIC_ACCESS_RIGHT[metric]
    : METRIC_ACCESS_RIGHT[metric];

/**
 * Couleur par grandeur, partagée entre le configurateur (icône de la tuile) et le widget
 * (icône + courbe). Source unique : sans ça, une grandeur aurait une teinte à la sélection et
 * une autre à l'affichage.
 *
 * ACCESSIBILITÉ (2026-07-30, clients malvoyants) : toute teinte ajoutée ici doit atteindre
 * **3:1 sur fond blanc** (seuil WCAG AA des composants graphiques) — un test le vérifie pour
 * chaque entrée de REGULATOR_METRIC_OPTIONS. Trois couleurs ont dû être assombries :
 * `temperatureInside` (l'olive de marque #A9B54B ne donnait que 2.23:1), `light` (2.15:1) et
 * `gaz` (2.95:1). Ne pas les « rétablir » à la charte sans revoir ce seuil.
 *
 * Int./ext. d'une même famille se distinguent par la LUMINANCE, pas par la teinte : deux
 * couleurs de luminance voisine ont un ratio ~1.1 entre elles, donc se confondent en niveaux
 * de gris comme pour un daltonien. D'où l'écart marqué #3F4A18 (9.5:1) vs #7a8a3a (3.8:1).
 */
export const METRIC_COLORS: Record<string, string> = {
  temperatureInside: "#3F4A18",
  temperatureOutside: "#7a8a3a",
  humidityInside: "#0976a5",
  humidityOutside: "#0b5b7d",
  co2: "#6B6B6B",
  nh3: "#b08968",
  airflow: "#4a9db5",
  light: "#9C7400",
  animalsTotal: "#6b7280",
  weight: "#B71C52",
  gmq: "#8e1a40",
  homogeneity: "#8e1a40",
  panel: "#8e1a40",
  dead: "#c0392b",
  water: "#1e90d6",
  food: "#B71C52",
  waterFeedRatio: "#1e90d6",
  silos: "#a0803a",
  siloStock: "#a0803a",
  gaz: "#AD4529",
  electricity: "#3d5a80",
};

/** Libellés lisibles des droits, pour l'indiquer à côté du titre de section. */
export const ACCESS_RIGHT_LABELS: Record<string, string> = {
  ambience: "Ambiance",
  mortality: "Mortalité",
  weighing: "Pesées",
  consumption: "Consommation",
  energy: "Énergie",
  // Droits legacy
  mesure: "Mesures",
  pesees: "Pesées",
  consogeneral: "Consommation",
};

/**
 * Grandeurs traçables sur la courbe. On écarte celles qui n'ont pas de série journalière
 * exploitable (`silos` est une liste, `waterFeedRatio` un rapport, `homogeneity`/`panel` ne
 * sont pas dans `RegulatorChartType`), et on ajoute `siloStock` — le stock total des silos,
 * qui se trace même si la LIGNE d'indicateur, elle, détaille silo par silo.
 */
export const REGULATOR_CHART_OPTIONS = [
  ...REGULATOR_METRIC_OPTIONS.filter(
    (option) =>
      !["silos", "waterFeedRatio", "homogeneity", "panel"].includes(option.value)
  ).map((option) => ({ value: option.value, label: option.label })),
  { value: "siloStock", label: "Stock silo (total)" },
];

// Une case à cocher par grandeur, groupée par famille : l'utilisateur compose son widget une
// fois pour toutes et n'y touche plus (plus d'onglets ni de sélecteur à l'affichage).
const REGULATOR_CONFIG_FIELDS: WidgetConfigField[] = [
  {
    key: "deviceUuid",
    label: "Sélectionner le régulateur",
    type: "select",
    // Régulateurs ACCESSIBLES à l'utilisateur (fetchAccessibleDevices) : tous pour un admin/psc,
    // uniquement ceux liés à son compte via LinkAccountDevice pour les autres types.
    // Pas de valueType : la référence est un uuid (string), pas un id numérique.
    dynamicOptions: "devices",
  },
  ...REGULATOR_METRIC_OPTIONS.map((option) => ({
    // `metric:` préfixe la clé pour que le configurateur sache qu'il s'agit d'une grandeur
    // du tableau `metrics` et non d'un champ de config indépendant.
    key: `metric:${option.value}`,
    label: option.label,
    type: "checkbox" as const,
    icon: option.icon as WidgetConfigField["icon"],
    group: option.group,
  })),
  {
    key: "showChart",
    label: "Afficher la courbe de tendance",
    type: "checkbox",
    icon: "chart-line",
    defaultValue: true,
    group: "chart",
  },
  {
    key: "chartType",
    label: "Grandeur tracée",
    type: "select",
    defaultValue: "temperatureInside",
    group: "chart",
    options: [...REGULATOR_CHART_OPTIONS],
  },
];

export const WIDGET_CATALOG: WidgetCatalogEntry[] = [
  {
    // L'id technique reste "RegulatorWidget" : il est SÉRIALISÉ dans `content.name` de chaque
    // widget posé (cf. Dashboard.tsx). Le renommer invaliderait tous les dashboards déjà
    // enregistrés pour un gain purement cosmétique. Seul le libellé visible change.
    id: "RegulatorWidget",
    label: "Régulateur",
    description:
      "Choisissez jusqu'à 8 grandeurs à suivre : le widget les affiche en permanence, sans manipulation.",
    defaultProps: REGULATOR_DEFAULTS,
    // 8 lignes = ce qui tient lisiblement dans une tuile avec la courbe en dessous.
    maxIndicators: 8,
    indicatorLimitGroups: ["ambience", "flock", "consumption", "energy"],
    // Plancher 4×8 : en dessous, 8 grandeurs + les vedettes + la courbe ne tiennent pas et le
    // contenu déborde (scroll vertical). Garanti au resize (minW/minH, GridStack) ET par les
    // tailles proposées, qui ne descendent plus sous ce seuil.
    minW: 4,
    minH: 8,
    sizes: [
      {
        id: "large",
        label: "6 × 11",
        w: 6,
        h: 11,
        layoutVariant: "full",
      },
      {
        id: "medium",
        label: "4 × 10",
        w: 4,
        h: 10,
        layoutVariant: "standard",
      },
      {
        id: "compact",
        label: "4 × 8",
        w: 4,
        h: 8,
        layoutVariant: "compact",
      },
    ],
    configFields: REGULATOR_CONFIG_FIELDS,
  },
  {
    id: "ChartWidget",
    label: "Graphique",
    description: "Courbes de température comparées entre bâtiments.",
    defaultProps: {
      title: "Température (°C)",
      periodLabel: "Aujourd'hui",
    } satisfies ChartWidgetProps,
    sizes: [
      { id: "large", label: "8 × 9", w: 8, h: 9 },
      { id: "medium", label: "6 × 6", w: 6, h: 6 },
      { id: "compact", label: "4 × 5", w: 4, h: 5 },
    ],
    configFields: [
      {
        key: "title",
        label: "Titre du graphique",
        type: "select",
        defaultValue: "Température (°C)",
        options: [
          { value: "Température (°C)", label: "Température (°C)" },
          { value: "Humidité (%)", label: "Humidité (%)" },
          { value: "Consommation d'eau", label: "Consommation d'eau" },
        ],
      },
      {
        key: "periodLabel",
        label: "Période affichée",
        type: "select",
        defaultValue: "Aujourd'hui",
        options: [
          { value: "Aujourd'hui", label: "Aujourd'hui" },
          { value: "7 jours", label: "7 jours" },
          { value: "30 jours", label: "30 jours" },
        ],
      },
    ],
  },
  {
    id: "WeatherWidget",
    label: "Météo",
    description: "Conditions météo locales et prévisions horaires.",
    // Chaque widget météo fait un appel API (Open-Meteo) → on en limite le nombre.
    maxInstances: 3,
    // Taille minimale : en dessous, le contenu météo devient illisible.
    minW: 2,
    minH: 4,
    defaultProps: {
      latitude: 47.996,
      longitude: -4.1024,
      locationName: "Quimper",
      showFeelsLike: true,
      showMinMax: true,
      showHumidity: true,
      showWind: true,
      showWindGusts: true,
      showPressure: true,
      showDewPoint: true,
      showThi: true,
      showUv: true,
      showRainProbability: true,
      showSunTimes: true,
      showHourly: true,
      showDaily: true,
    } satisfies WeatherWidgetProps,
    sizes: [
      { id: "large", label: "4 × 5", w: 4, h: 5 },
      { id: "medium", label: "4 × 4", w: 4, h: 4 },
      { id: "compact", label: "3 × 3", w: 3, h: 3 },
    ],
    configFields: [
      { key: "locationName", label: "Ville", type: "citySearch" },
      {
        key: "showFeelsLike",
        label: "Ressenti",
        type: "checkbox",
        icon: "temperature-half",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showMinMax",
        label: "Min / Max du jour",
        type: "checkbox",
        icon: "arrows-up-down",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showHumidity",
        label: "Humidité",
        type: "checkbox",
        icon: "droplet",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showWind",
        label: "Vent",
        type: "checkbox",
        icon: "wind",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showWindGusts",
        label: "Rafales de vent",
        type: "checkbox",
        icon: "wind",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showPressure",
        label: "Pression atmosphérique",
        type: "checkbox",
        icon: "gauge",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showDewPoint",
        label: "Point de rosée",
        type: "checkbox",
        icon: "droplet",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showThi",
        label: "Stress thermique (THI)",
        type: "checkbox",
        icon: "temperature-high",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showUv",
        label: "Indice UV",
        type: "checkbox",
        icon: "sun",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showRainProbability",
        label: "Probabilité de pluie",
        type: "checkbox",
        icon: "umbrella",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showSunTimes",
        label: "Lever / coucher du soleil",
        type: "checkbox",
        icon: "sun",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showHourly",
        label: "Prévisions horaires",
        type: "checkbox",
        icon: "clock",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showDaily",
        label: "Prévisions journalières (5-7 j)",
        type: "checkbox",
        icon: "calendar-days",
        defaultValue: true,
        group: "indicators",
      },
    ],
  },
  {
    id: "ContactsWidget",
    label: "Contacts",
    description: "Accès rapide aux contacts de l'exploitation.",
    maxInstances: 1,
    defaultProps: {},
    sizes: [
      { id: "large", label: "4 × 6", w: 4, h: 6 },
      { id: "medium", label: "4 × 5", w: 4, h: 5 },
      { id: "compact", label: "3 × 4", w: 3, h: 4 },
    ],
    configFields: [],
  },
  {
    id: "AlarmsWidget",
    label: "Alarmes",
    description: "Historique et alertes actives du site.",
    defaultProps: { totalCount: 12 },
    sizes: [
      { id: "large", label: "4 × 6", w: 4, h: 6 },
      { id: "medium", label: "4 × 5", w: 4, h: 5 },
      { id: "compact", label: "3 × 4", w: 3, h: 4 },
    ],
    configFields: [],
  },
  {
    id: "NewsWidget",
    label: "Actualités",
    description:
      "Fil d'actualités défilant (avicole, climat, marché, réglementation).",
    // Bandeau fin : large par défaut, hauteur d'une ligne.
    minW: 3,
    minH: 1,
    defaultProps: {
      category: "all",
      speed: "normal",
      showSource: true,
      showDate: true,
    } satisfies NewsWidgetProps,
    sizes: [
      { id: "large", label: "6 × 2", w: 6, h: 2 },
      { id: "medium", label: "4 × 2", w: 4, h: 2 },
      { id: "compact", label: "3 × 1", w: 3, h: 1 },
    ],
    configFields: [
      {
        key: "category",
        label: "Catégorie",
        type: "select",
        defaultValue: "all",
        options: [
          { value: "all", label: "Toutes" },
          ...Object.entries(NEWS_CATEGORY_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
        ],
      },
      {
        key: "speed",
        label: "Vitesse de défilement",
        type: "select",
        defaultValue: "normal",
        options: [
          { value: "slow", label: "Lent" },
          { value: "normal", label: "Normal" },
          { value: "fast", label: "Rapide" },
        ],
      },
      {
        key: "showSource",
        label: "Afficher la source",
        type: "checkbox",
        icon: "newspaper",
        defaultValue: true,
        group: "indicators",
      },
      {
        key: "showDate",
        label: "Afficher la date",
        type: "checkbox",
        icon: "clock",
        defaultValue: true,
        group: "indicators",
      },
    ],
  },
  {
    id: "TextWidget",
    label: "Texte",
    description: "Un titre ou une note pour organiser et annoter le dashboard.",
    defaultProps: {
      text: "Titre",
      style: "title",
      align: "center",
      background: "transparent",
    } satisfies TextWidgetProps,
    sizes: [
      { id: "large", label: "6 × 2", w: 6, h: 2 },
      { id: "medium", label: "4 × 2", w: 4, h: 2 },
      { id: "compact", label: "3 × 1", w: 3, h: 1 },
    ],
    configFields: [
      {
        key: "text",
        label: "Texte",
        type: "text",
        placeholder: "Ex. Bâtiments",
      },
      {
        key: "style",
        label: "Style",
        type: "select",
        defaultValue: "title",
        options: [
          { value: "title", label: "Titre" },
          { value: "subtitle", label: "Sous-titre" },
          { value: "body", label: "Texte" },
        ],
      },
      {
        key: "align",
        label: "Alignement",
        type: "select",
        defaultValue: "center",
        options: [
          { value: "left", label: "Gauche" },
          { value: "center", label: "Centré" },
          { value: "right", label: "Droite" },
        ],
      },
      {
        key: "background",
        label: "Fond",
        type: "select",
        defaultValue: "transparent",
        options: [
          { value: "card", label: "Carte" },
          { value: "transparent", label: "Transparent" },
        ],
      },
    ],
  },
];

export function getWidgetCatalogEntry(
  widgetType: string
): WidgetCatalogEntry | undefined {
  return WIDGET_CATALOG.find((entry) => entry.id === widgetType);
}

/** Contraintes de taille minimale (cellules) d'un type de widget, si définies. */
export function getWidgetMinSize(widgetType: string): {
  minW?: number;
  minH?: number;
} {
  const entry = getWidgetCatalogEntry(widgetType);
  return { minW: entry?.minW, minH: entry?.minH };
}

export function getWidgetSizeOption(
  entry: WidgetCatalogEntry,
  sizeId: string
): WidgetSizeOption | undefined {
  return entry.sizes.find((size) => size.id === sizeId);
}

export function findSizeByDimensions(
  entry: WidgetCatalogEntry,
  w: number,
  h: number
): WidgetSizeOption {
  return (
    entry.sizes.find((size) => size.w === w && size.h === h) ?? entry.sizes[0]
  );
}

export function buildDefaultWidgetProps(
  entry: WidgetCatalogEntry
): Record<string, unknown> {
  return { ...entry.defaultProps };
}

export function buildWidgetPropsFromConfig(
  entry: WidgetCatalogEntry,
  config: Record<string, unknown>,
  size?: WidgetSizeOption
): Record<string, unknown> {
  const props = buildDefaultWidgetProps(entry);

  // Les valeurs de config (champs + références comme buildingId / libellé) priment.
  // Une clé PRÉSENTE dans la config s'applique même si sa valeur est `undefined` : c'est un
  // choix explicite de l'utilisateur (ex. retirer une vedette à la punaise), et l'ignorer ferait
  // réapparaître le défaut du catalogue — la seconde vedette revenait ainsi après décochage.
  Object.keys(config).forEach((key) => {
    props[key] = config[key];
  });

  if (size?.layoutVariant) {
    props.layoutVariant = size.layoutVariant;
  }

  return props;
}
